/**
 * Достижения и уровни (PLAN §3.1, §3.8). Здесь только типы: и ответа API, и
 * «цели» карточки (data-ach-card), и публичного каталога, который F1
 * генерирует в catalog.gen.ts (см. model/catalog.ts — граница импорта).
 */

/** Языки каталога. Сайт: cn → zh, остальные незнакомые → en (lib/lang.ts). */
export type AchLang = "ru" | "en" | "de" | "es" | "zh";

/**
 * Языки текстов достижений на экране: языки каталога и ar — арабский интерфейс (только при
 * включённом рубильнике или предпросмотре, shared/config/arabicUi; арабские тексты каталога —
 * lib/arCatalog.ts). Сервер и кэши — по AchLang (achLang: ar → en); тексты — по achUiLang.
 */
export type AchUiLang = AchLang | "ar";

/** Сферы в порядке шаров профиля (ScoresRow). Уровень тренировок — общий уровень их шкалы (лестница workouts). */
export type AchSphere = "contrib" | "workouts" | "social" | "referrals" | "learning";

export const ACH_SPHERES: readonly AchSphere[] = ["contrib", "workouts", "social", "referrals", "learning"];

/**
 * Шкала «Тренировки» (ADDENDUM 30.09 §6–§7) — всё по плану тренировок: общий уровень
 * (workouts — недели по плану; он же в ряду уровней, у аватара и в значках) и четыре
 * лестницы практик по плану — ходьба и бег (dyn), статика (stat), тепло (therm), пост (fast).
 */
export type AchLadder = "workouts" | "dyn" | "stat" | "therm" | "fast";

export const ACH_LADDERS: readonly AchLadder[] = ["workouts", "dyn", "stat", "therm", "fast"];

/** Всё, у чего есть уровни 1–6: сфера с баллами или лестница тренировок (цель карточки «lvl»). */
export type AchLevelKey = AchSphere | AchLadder;

export const ACH_LEVEL_KEYS: readonly AchLevelKey[] = [...ACH_SPHERES, "dyn", "stat", "therm", "fast"];

/** Форма названия по полу человека, которого показываем; null — неизвестно (мужская форма). */
export type AchForm = "f" | "m";

// ── Ответ API (§3.6 /api/achievements/me, проекция для других) ─────────────

export interface AchItem {
    key: string;
    tier?: number;
    at?: string;
    /** Текущее значение — только в своём профиле. */
    value?: number;
    /** Порог следующей ступени — только в своём профиле. */
    next?: number;
    /** Доля участников с этой ступенью, % (ночная сводка сервера); нет — не известна. */
    share?: number;
}

export interface AchCollectionRow {
    key: string;
    year?: number;
    event?: string;
    city?: string;
    note?: string;
    /** Доля участников с этой строкой (ключ + год), %; нет — не известна. */
    share?: number;
}

export interface AchLevel {
    level: number;
    at?: string;
    /** Баллы сферы — только в своём профиле. */
    points?: number;
    next?: number;
}

/**
 * Лестница тренировок в ответе сервера. Свой вид: {level, at?, value?, next?,
 * variant?, img}; чужой: {level, img} — без значений и порогов (R2).
 */
export interface AchLadderState {
    level: number;
    at?: string;
    /** Практики по плану (у общего — недели по плану) — только в своём профиле. */
    value?: number;
    /** Порог следующего уровня в той же единице — только в своём профиле. */
    next?: number;
    /** Упражнение картинки статики и тепла (то, что у человека чаще): water, pose, tree; bath, hot. */
    variant?: string;
    /** id картинки текущего уровня по полу и упражнению человека (у 0 — первая). */
    img?: string;
}

/** Ступень (уровень) строки цели: подпись («3 раза»; у уровней — название в форме человека) и картинка. */
export interface AchGoalStep {
    n: number;
    label: string;
    img?: string;
}

/**
 * Строка «что сделать → что получите» от сервера (GET /api/achievements/app/me):
 * nearest[] — только начатое, goals[] — начатое и ещё не начатое («ступень 1»).
 * line — готовая строка сайта на языке запроса; img — id картинки цели (рисуется
 * в цвете, владелец 01.10); link — ключ ссылки «куда идти» (AchLinkKey); anchor —
 * сфера страницы достижений; what — коротко «за что» («Баня, сауна и хаммам»);
 * current — сколько ступеней (уровень) уже взято; steps — все ступени для окна с
 * пояснением. what / current / steps сервер присылает не сразу — их может не быть.
 */
export interface AchGoal {
    /** Ключ достижения или «lvl:<сфера | лестница>». */
    id: string;
    /** tier | pace | level | ladder */
    kind: string;
    key: string;
    sphere: string;
    name: string;
    /** Ступень (уровень), которая будет взята, и сколько их всего. */
    n: number;
    of: number;
    value?: number;
    next?: number;
    remaining: number;
    /** Доля пути, 0…1. */
    ratio: number;
    line: string;
    img?: string;
    link?: string;
    anchor?: string;
    /** false — ещё не начато (сервер подставил ноль). */
    started: boolean;
    /** «Баня, сауна и хаммам»; у уровней и лестниц — что они считают (без точки в конце). */
    what?: string;
    /** Сколько ступеней (уровень) уже взято: 0 — ничего; всегда n − 1. */
    current?: number;
    /** Все ступени (уровни) с подписью и картинкой. */
    steps?: AchGoalStep[];
    /**
     * Доля участников, у кого ступень n уже есть, % — «IV ступень есть у 1 % участников»;
     * null — ни у кого («IV ступень пока ни у кого»); нет поля — доля не известна (сводки ещё нет).
     */
    shareNext?: number | null;
}

export interface AchievementsView {
    self: boolean;
    form: AchForm | null;
    levels?: Partial<Record<AchSphere, AchLevel>>;
    /** Шкала «Тренировки» (ADDENDUM §6): приходит вместе с levels, тем, кому их показывают. */
    ladders?: Partial<Record<AchLadder, AchLadderState>>;
    items: AchItem[];
    collection: AchCollectionRow[];
    top?: { key: string; tier?: number }[];
    /** Только свой вид из /app/me: цели (начатые первыми) — если сервер их уже присылает. */
    goals?: AchGoal[];
    /** Только свой вид из /app/me: «Ближайшие» сервера (только начатое) — запас, пока целей нет. */
    nearest?: AchGoal[];
    /**
     * Только свой вид из /app/me (профиль v2 §1.3): «Ближе всего» — до двух строк ступеней и
     * темпа (без уровней, лестниц и «Своего герба»), в порядке сервера.
     */
    closest?: AchGoal[];
    /**
     * Только свой вид из /app/me (профиль v2 §1.3, §3.7): строка цели «Свой герб» (own_crest) —
     * «Добавьте фото и пару слов о себе — и вы получите «Свой герб»». Есть, пока сервер её
     * присылает (награда считается и не получена); в общие цели (goals) не попадает.
     */
    crest?: AchCrestHint;
}

/** Строка «Своего герба» (own_crest) из goals[] /app/me: готовая строка сервера и картинка. */
export interface AchCrestHint {
    line: string;
    img?: string;
}

// ── Цель карточки: атрибут data-ach-card у любой картинки (§3.8) ───────────

/**
 * - lvl — уровень сферы или лестницы тренировок s, n — номер уровня; pts — баллы
 *   сферы, v — значение лестницы (практики или недели по плану) — только self; x —
 *   упражнение картинки статики и тепла (water, pose, tree; bath, hot).
 * - ach — достижение k; tier — ступень; v — текущее значение (только self);
 *   y / e / c / note — строка коллекции (год, событие, город, заметка администратора).
 * - top — тема умного поиска или её дополнение (topic_title и др.).
 * self: 1 — свой профиль (строка прогресса); lock: 1 — ещё не получено (рисуется
 * в цвете, с пунктирной рамкой — владелец 01.10: «серое непонятно, что это»).
 * cur — сколько ступеней (уровней) уже взято: фишки «взято» в карточке; нет — у
 * взятой это её ступень, у невзятой — на одну меньше.
 * f — форма названия (у уровней и у «Сведущий в …»), у тренировок — и картинки.
 * Окно достижения v2 (профиль v2 §5.3) у взятого — что известно с той стороны, откуда нажали
 * (плитка профиля v2, свой вид); нет — строки окна просто нет:
 *   r — редкость 1–4 от сервера (подпись и рамка; нет — та же по каталогу, lib/significance);
 *   sh — «есть у N % участников» (доля с этой ступенью, у строки коллекции — за её год);
 *   at — «Получено 21 сентября 2026» (дата показанной ступени, «ГГГГ-ММ-ДД»).
 */
export type CardTarget =
    | {
          t: "lvl";
          s: AchLevelKey;
          n: number;
          f?: AchForm;
          x?: string;
          self?: 1;
          pts?: number;
          v?: number;
          lock?: 1;
          cur?: number;
      }
    | {
          t: "ach";
          k: string;
          tier?: number;
          self?: 1;
          v?: number;
          lock?: 1;
          cur?: number;
          y?: number;
          e?: string;
          c?: string;
          note?: string;
          r?: AchRarity;
          sh?: number;
          at?: string;
      }
    | {
          t: "top";
          k: string;
          tier?: number;
          self?: 1;
          v?: number;
          lock?: 1;
          cur?: number;
          f?: AchForm;
          r?: AchRarity;
          sh?: number;
          at?: string;
      };

/** Редкость взятого: 1 Памятная · 2 Редкая · 3 Эпическая · 4 Легендарная (rarity каталога). */
export type AchRarity = 1 | 2 | 3 | 4;

// ── Публичный каталог (§3.1; catalog.public.json → catalog.gen.ts) ─────────

/**
 * Текст на пяти языках: ru/en/de/es/zh, у названий ещё ru_f/de_f/es_f.
 * Нестрогий (значение может отсутствовать), чтобы в него входил и строгий тип F1
 * (model/catalogTypes.ts: AchText & Partial<Record<"ru_f" | …, string>>).
 */
export type AchText = Readonly<Record<string, string | undefined>>;

export interface AchTierDef {
    readonly tier: number;
    readonly value: number;
    readonly label: AchText;
}

export interface AchEntry {
    readonly key: string;
    readonly sphere: string;
    readonly facet: string;
    /** "tiered" | "one_off" | "commemorative" | "admin_granted". */
    readonly kind: string;
    readonly name: AchText;
    readonly what: AchText;
    /** "max" (больше — лучше) или "min" (темп: меньше — лучше). */
    readonly direction?: string;
    readonly unit?: string;
    readonly tiers?: readonly AchTierDef[];
    /** Подпись с годом/событием у памятных и вручаемых: «Огни фестиваля · {event} {year}». */
    readonly label?: AchText;
    readonly img: readonly string[];
    /** "admin" | "claim" — у вручаемых. */
    readonly grant_mode?: string;
    readonly admin_can_grant?: boolean;
    readonly private_result?: boolean;
}

export interface AchTopic {
    readonly key: string;
    readonly name: AchText;
    readonly topic?: AchText;
    readonly what: AchText;
    readonly title?: AchText;
    readonly sensitive?: boolean;
    readonly unit?: string;
    readonly tiers: readonly AchTierDef[];
    readonly img: readonly string[];
}

export interface AchTopicExtra {
    readonly key: string;
    readonly name: AchText;
    readonly what: AchText;
    readonly img: readonly string[];
    /** Награда за интересы: видна тем, кому видны «Интересы» (профиль v2 §1.7.2). */
    readonly interests?: boolean;
}

export interface AchLevelDef {
    readonly n: number;
    readonly points: number;
    readonly img: string;
    readonly name: AchText;
    readonly approx: AchText;
}

export interface AchLevelSphere {
    readonly scale: number;
    readonly general: AchText;
    readonly levels: readonly AchLevelDef[];
}

/** Уровень лестницы тренировок (ADDENDUM §6–§7): порог в её единице, «за что», картинки по полу и упражнению. */
export interface AchLadderLevelDef {
    readonly n: number;
    /** Уровень 1 — первая практика (у общего — первая неделя) по плану, без числа. */
    readonly first?: boolean;
    /** Уровни 2–6: порог в единице лестницы. */
    readonly value?: number;
    /** «5 практик ходьбы и бега по плану», «12 недель по плану» — «за что» карточки. */
    readonly label: AchText;
    readonly name: AchText;
    /** id картинки: по форме («f» / «m»), у статики и тепла — по «<упражнение>-<f|m>». */
    readonly img: Readonly<Record<string, string>>;
}

export interface AchLadderDef {
    readonly sphere: string;
    /** «Ходьба и бег», «Тренировки — общий уровень». */
    readonly title: AchText;
    /** Что считает лестница — приглушённая строка карточки. */
    readonly general: AchText;
    /** Ключ units каталога: «{n} практик ходьбы и бега по плану», «{n} недель по плану». */
    readonly unit: string;
    /** Было у общего уровня §6 («общий путь» из трёх видов); с §7 каталог его не пишет. */
    readonly from?: readonly string[];
    /** Упражнения картинки (статика, тепло); при равенстве — первое. */
    readonly variants?: readonly string[];
    readonly variant_names?: Readonly<Record<string, AchText>>;
    /** «Картинка — «{variant}»: то, что у вас чаще.» */
    readonly picture_note?: AchText;
    readonly levels: readonly AchLadderLevelDef[];
}

/**
 * Формы единицы по языку: {"ru": {"one": "{n} неделя", "few": …}, …}; format "pace" — у темпа.
 * Значение по языку разбирает lib/cardModel (unitText): у F1 это интерфейс AchPluralForms.
 */
export type AchUnit = { readonly format?: string } & { readonly [lang: string]: unknown };

export interface AchCatalog {
    readonly schema: number;
    readonly version: string;
    readonly img_ver: string;
    readonly langs: readonly string[];
    readonly rarity: readonly { readonly tier: number; readonly name: AchText }[];
    readonly facets: Readonly<Record<string, AchText>>;
    readonly spheres: Readonly<Record<string, AchText>>;
    readonly units: Readonly<Record<string, AchUnit>>;
    readonly achievements: readonly AchEntry[];
    readonly topics: readonly AchTopic[];
    readonly topic_extras: readonly AchTopicExtra[];
    readonly levels: {
        readonly steps_pct: readonly number[];
        readonly order: readonly string[];
        readonly reserved: readonly string[];
        readonly spheres: Readonly<Partial<Record<AchSphere, AchLevelSphere>>>;
        /** Шкала «Тренировки» (ADDENDUM §6–§7); её общий уровень (workouts) стоит в ряду уровней. */
        readonly ladders?: Readonly<Partial<Record<AchLadder, AchLadderDef>>>;
    };
}
